import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';
import { interval, ReplaySubject, Subscription } from 'rxjs';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { AppService } from '@irb/app.service';
import { IrbSettingsEvents, IrbCreatePortfolioEvents, IrbDialogsEvents, IrbAppEvents, IEventSubscriber, IEvent } from '@irb-common/events';
import { IrbDashboardService } from '@irb/irb-dashboard/irb-dashboard.service';
import { IGlobalSettings, IAdvisorInvoice, ISystemMessages } from '@irb/irb-dashboard/irb-dashboard.model';
import { PageService } from '@irb-common/services/page.service'; 
import { DataTransferService } from '@irb/irb-common/services/data-transfer.service';
import { IUserDetails } from '@irb/app.model';
import { AnalyticsService } from '@irb-common/analytics/analytics.service';
import { IrbSettingsService } from '@irb/irb-settings/irb-settings.service';
import {EditorModelService} from "@irb/irb-editors/editor-model/editor-model.service";
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import {AssignedAccountsService} from "@irb/irb-clients/assigned-accounts/assigned-accounts-service";
import {IDropdownItem} from "@irb-common/models/dropdown.model";
import {PreferencesService} from "@irb-common/services/preferences.service";
import {ModuleClientsComponent} from "@irb/irb-dashboard/module-clients/module-clients.component";
import {
  ModuleAutoRebalancingWidgetComponent
} from "@irb/irb-dashboard/module-auto-rebalancing-widget/module-auto-rebalancing-widget.component";
import {ModuleBlotterComponent} from "@irb/irb-dashboard/module-blotter/module-blotter.component";
import {ModuleRebalancingComponent} from "@irb/irb-dashboard/module-rebalancing/module-rebalancing.component";
import {ModuleCashAnalysisComponent} from "@irb/irb-dashboard/module-cash-analysis/module-cash-analysis.component";
import {ModuleModelsComponent} from "@irb/irb-dashboard/module-models/module-models.component";
import {ModuleMmcComponent} from "@irb/irb-dashboard/module-mmc/module-mmc.component";
import {ModuleAllocationComponent} from "@irb/irb-dashboard/module-allocation/module-allocation.component";

@Component({ standalone: false,
  selector: 'irb-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit, OnDestroy, IEventSubscriber {
  @ViewChild("bannerComponent") bannerComponent: any;
  @ViewChild("masterAccountDropDown") masterAccountDropDown: any;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  globalSettings: IGlobalSettings;
  modelsWithUnreadUpdatesCount: number;
  advisorInvoiceList: IAdvisorInvoice[];
  advisorRecentInvoiceList: IAdvisorInvoice[];
  advisorPreviousInvoiceList: IAdvisorInvoice[];
  invoice: IAdvisorInvoice;
  previousInvoiceSelected: number = -1;
  showDITile: boolean = false;
  userDetails: IUserDetails ;
  private timeInterval:Subscription;
  timerOn:boolean
  eventSubscription: Subscription;
  isShowAutoTrading = true;
  masterAccounts: any;
  masterAccountList: Array<IDropdownItem> = null;
  masterAccountsBeforeChange: any;
  isAccountsSet: boolean = false;
  enableCustomHomepage: boolean = false;
  msPrefValue: any = null;
  showAsAllForMADD: boolean = true;
  modulesToBuild: any = [];
  modulesDefaultConfig: any = [];
  isUiTest:boolean = false;

  constructor(private as: AppService, public ps: PageService, private dbs: IrbDashboardService, private router: Router, public dataTransferService: DataTransferService,
              public ats: AnalyticsService, public iss:IrbSettingsService, private editorModelService: EditorModelService,
              private cdr: ChangeDetectorRef, private aas: AssignedAccountsService, private pref: PreferencesService) {
    this.iss.importSecJobTimerOn$.subscribe(data => {
      this.timerOn = data;
    });

    this.eventSubscription = this.as.getEvents().subscribe((s) => this.processEvent(s));

  }

  processEvent(event: IEvent): void {
    switch(event.type) {
      case IrbAppEvents.FIRE_TRACKING_EVENT: 
        this.ats.trackHomeEvent();
        break;
    }
  }

  ngOnInit() {
    this.ats.trackHomeEvent();
    this.setMAValuesfromPreferences();
    this.ps.getUserDetails().pipe(takeUntil(this.destroyed$)).subscribe((ud_data: any) => {
      if(ud_data) {
        this.enableCustomHomepage = ud_data.featureFlags?.ENABLE_CUSTOM_HOMEPAGE || false;
        // this.enableCustomHomepage=false;
        if (!this.isAccountsSet && this.enableCustomHomepage)
          this.setMAValuesfromPreferences();

        if (this.enableCustomHomepage)
          this.collectModulesToBuild();

        this.iss.getGlobalSettings().pipe(takeUntil(this.destroyed$)).subscribe((data) => {
          this.globalSettings = data as IGlobalSettings;
          this.dataTransferService.EnvironmentSpecificCustomCategory = this.globalSettings.securityCustAttrAbbr;
          this.dataTransferService.PortfolioCustomCategory = this.globalSettings.portfolioCustLabelAbbr;
          this.dataTransferService.ShowExternalAccounts = this.globalSettings.showExternalAccounts;
          this.dataTransferService.ShowSMASymbol = this.globalSettings.isSMAallowed;
          this.dataTransferService.advisorImposedBuyMin = this.globalSettings.advisorImposedBuyMin;
          this.dataTransferService.advisorImposedSellMin = this.globalSettings.advisorImposedSellMin;
          this.dataTransferService.advisorImposedBuyMinPercent = this.globalSettings.advisorImposedBuyMinPercent;
          this.dataTransferService.advisorImposedSellMinPercent = this.globalSettings.advisorImposedSellMinPercent;
          this.getSystemMessages();
          this.isShowAutoTrading = ud_data.isShowAutoTrading && this.globalSettings.isAutoRebalanceEnabled;
          if (!this.isShowAutoTrading)
            this.hideAutoTradingModule();

          this.editorModelService.setGlobalSettings(data);

        });

        if (this.enableCustomHomepage) {
          this.aas.getVisibleMasterAccounts().subscribe({
            next: (res: any) => {
              const masterAccountPreference: any = this.pref.getPreferenceByName('dashboard-master-account');
              if (masterAccountPreference && masterAccountPreference.preferenceValue && JSON.stringify(masterAccountPreference.preferenceValue) !== '{}') {
                this.msPrefValue = masterAccountPreference.preferenceValue;
              }
              if (res && res.data && res.data.irebalResponse) {
                const maList: Array<string> = res.data.irebalResponse.sort(this.compare);
                this.masterAccountList = [];
                maList.forEach(ma => {
                  this.masterAccountList.push({name: ma, value: ma});
                });
                this.masterAccounts = masterAccountPreference && this.msPrefValue ? this.msPrefValue : JSON.parse(JSON.stringify(this.masterAccountList));

                if (!masterAccountPreference)
                  this.saveMasterAccountPreference(this.masterAccounts);
                this.isAccountsSet = true;
                this.masterAccountsBeforeChange = JSON.parse(JSON.stringify(this.masterAccountList));
                if (this.masterAccounts.length !== this.masterAccountList.length) {
                  this.dbs.selectedMasterAccounts = this.masterAccounts.length > 0 ? this.masterAccounts.map((account: IDropdownItem) => account.value) : null;

                }
                this.cdr.detectChanges();
              }
            },
            error: () => {
              this.masterAccountList = [];
              this.cdr.detectChanges();
            }
          });
        } else {
          this.isAccountsSet = true;
        }
      }
    });

    this.userDetails = this.as.getItem('IRB_APP_CONFIG') as IUserDetails;

    if(this.userDetails != null) {
      this.showDITile = this.userDetails.isDIEnabled;
    }
    
    if(this.timerOn){
      this.showImportSecuritiesAlert(true)
      this.iss.importSecJobTimerOn$.next(false);
      this.iss.importSecJobTimerOn$.complete();
    }
  }

  setMAValuesfromPreferences() {
    const masterAccountPreference: any = this.pref.getPreferenceByName('dashboard-master-account');
    if (masterAccountPreference && masterAccountPreference.preferenceValue && JSON.stringify(masterAccountPreference.preferenceValue) !== '{}') {
      this.showAsAllForMADD = false;
      this.msPrefValue = masterAccountPreference.preferenceValue;
      if (masterAccountPreference && this.msPrefValue) {
        this.masterAccounts = this.msPrefValue;
        this.dbs.selectedMasterAccounts = this.masterAccounts.length > 0 ? this.masterAccounts.map((account: IDropdownItem) => account.value) : null;
        this.isAccountsSet = true;
      }
    }
    if (masterAccountPreference && masterAccountPreference.preferenceValue && JSON.stringify(masterAccountPreference.preferenceValue) === '{}') {
      this.showAsAllForMADD = true;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.eventSubscription.unsubscribe();
  }

  alertsUpdate() {
    if(this.bannerComponent) {
      this.bannerComponent.getCount(["MMC_MODEL_ALLOCATION_UPDATE","MMC_MODEL_STRATEGIST_FEE_UPDATE"]).pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
        this.modelsWithUnreadUpdatesCount = data;
      });
    }
  }

  viewRecentInvoice(id: number) {
    window.open("/irebal/mmc/get-advisor-invoice/" + id, "_blank");
  }

  viewPreviousInvoice(event: any) {
    if(event.value !== -1) {
      window.open("/irebal/mmc/get-advisor-invoice/" + event.value, "_blank");
      
      window.setTimeout(() => {
        this.previousInvoiceSelected = -1;
      });
    }
  }

  setRecentAdvisorList() {
    this.advisorRecentInvoiceList = this.advisorInvoiceList.filter(this.isMostRecent);     
  }

  setPreviousAdvisorList() {
    this.advisorPreviousInvoiceList = this.advisorInvoiceList.filter(this.isPrevious);    
  }

  isMostRecent(_element:IAdvisorInvoice, index: number, _array: IAdvisorInvoice[]) { 
    return (index <= 2); 
  } 

  isPrevious(_element:IAdvisorInvoice, index: number, _array: IAdvisorInvoice[]) { 
    return (index > 2); 
  } 
  
  showModelsWithUnreadChanges() {
    this.ps.pageState.modelGridDiv["filterData"]["filterSlctIsRead"] = false;
    this.ps.pageState.modelGridDiv["filterString"] = "isRead=false";
    this.ps.serializePageState();
    this.router.navigate(['/models/models']);
  }

  showPortfolios() {
    this.router.navigate(['/portfolios']);
  }

  showPortfoliosWithPendingResults() {
    this.router.navigate(['/portfolios']);
  }

  showPortfoliosWithHighPriorityNotes() {
    this.router.navigate(['/portfolios']);
  }

  showPortfoliosWithNoModels() {
    this.ps.pageState.portfolioGridDiv["filterData"]["slctAssignedModel"] = "None";
    this.ps.pageState.portfolioGridDiv["filterString"] = "withoutModel=null";
    this.ps.serializePageState();
    this.router.navigate(['/portfolios']);
  }

  showTaxHarvestExpiration() {
    this.router.navigate(['/portfolios']);
  }

  showScheduledRebalanceExpiration() {
    this.ps.pageState.scheduledRebalancesGridDiv["rebalanceKey"] = "4";
    this.ps.serializePageState();
    this.router.navigate(['/rebalancing/scheduled-rebalances']);
  }

  showScheduledRebalances() {
    this.ps.pageState.scheduledRebalancesGridDiv["rebalanceKey"] = "7";
    this.ps.serializePageState();
    this.router.navigate(['/rebalancing/scheduled-rebalances']);
  }

  showModels() {
    this.ps.pageState.modelGridDiv["useDefaultTab"] = "true";
    this.ps.serializePageState();
    this.router.navigate(['/models/models']);
  }

  showIncompleteModels() {
    this.ps.pageState.modelGridDiv["useDefaultTab"] = "true";
    this.ps.pageState.modelGridDiv.filterData["filterSlctStatus"] = "incomplete";
    this.ps.pageState.modelGridDiv["filterString"] = "status=incomplete";
    this.ps.serializePageState();
    this.router.navigate(['/models/models']);
  }

  showDraftModels() {
    this.ps.pageState.modelGridDiv["useDefaultTab"] = "true";
    this.ps.pageState.modelGridDiv.filterData["filterSlctStatus"] = "draft";
    this.ps.pageState.modelGridDiv["filterString"] = "status=draft";
    this.ps.serializePageState();
    this.router.navigate(['/models/models']);
  }

  showRefusedModels() {
    this.ps.pageState.modelGridDiv["useDefaultTab"] = "true";
    this.ps.pageState.modelGridDiv.filterData["filterSlctStatus"] = "refused";
    this.ps.pageState.modelGridDiv["filterString"] = "status=refused";
    this.ps.serializePageState();
    this.router.navigate(['/models/models']);
  }

  showOrderBlotter() {
    this.router.navigate(['/orders']);
  }

  showGlobalSettingsDialog() {
    this.as.fireEvent(IrbSettingsEvents.SHOW);
  }

  createNewPortfolio() {
    this.as.fireEvent(IrbCreatePortfolioEvents.SHOW_CREATE_PORTFOLIO);
  }

  getSystemMessages() {
    if(!this.as.getFromSession("systemMessagesDisplayed")) {
      this.dbs.getSystemMessages().pipe(takeUntil(this.destroyed$)).subscribe((data: ISystemMessages) => {
        //this.ats.trackLoginEvent();
        
        if(data.systemMessages.length > 0) {
          if(data.lastModified) {
            data.lastModified = data.lastModified.replace('EST','ET');
          }

          this.as.setToSession("systemMessages",data);
          this.as.setToSession("systemMessagesDisplayed",true);
          this.as.fireEvent(IrbDialogsEvents.SHOW_ANNOUNCEMENTS);
        }
      });
    }
  }


  showImportSecuritiesAlert(offTimer:boolean){
      this.timeInterval = interval(5000).pipe(
        startWith(0),
        switchMap(() => this.iss.checkImportSecuritiesJobStatus())).subscribe((data: any) => {
        if (data.responseUtilityMap.isJobCompleted === "FAILED") {
          this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            key:'irb-import-sec-job-message',
            severity: 'error',
            summary: 'Securities import failed',
            detail: "Please contact the support team at...",
            sticky:true
        });
        this.timeInterval.unsubscribe();
        } else if (data.responseUtilityMap.isJobCompleted === "COMPLETED"){
          this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
              key: 'irb-import-sec-job-message',
              severity: 'success',
              summary: 'Import Success',
              detail: "Your securities inventory is now populated",
              sticky:true
            });
            this.timeInterval.unsubscribe();
        }else if (data.responseUtilityMap.isJobCompleted === "NO_REQUEST" && offTimer){
          //No need to show any toast message.
          this.timeInterval.unsubscribe();
        }
      });
  }

  setMasterAccountDropDown(searchStr: any) {
    this.masterAccountDropDown.set(DropdownFilterComponent.getFilteredList(searchStr, this.masterAccountList));
  }

  onMasterChange() {
    let updatedData = this.masterAccounts && this.masterAccounts.length !== this.masterAccountList.length ? this.masterAccounts : null;
    if (this.masterAccounts && this.masterAccounts.length === 0) {
      this.as.fireEvent(IrbAppEvents.SHOW_ALERT,{
        dTitle: 'Master Accounts',
        dType: 'WARN',
        msg:  "Selection of master account(s) cannot be empty."
      });
      this.masterAccounts = JSON.parse(JSON.stringify(this.masterAccountsBeforeChange));
      return;
    }
    if (this.masterAccounts && this.masterAccounts.length) {
      if (this.masterAccounts.length === this.masterAccountList.length) {
        this.masterAccountDropDown.showAsAll = true;
      } else {
        this.masterAccountDropDown.showAsAll = false;
      }
    }
    if (!this.masterAccounts) {
      this.masterAccountDropDown.hide();
      this.masterAccountDropDown.isAllSelected = true;
      this.masterAccountDropDown.showAsAll = true;
      this.masterAccounts = JSON.parse(JSON.stringify(this.masterAccountList));
      updatedData = null;
    }
    this.dbs.updateMasterAccountsData(updatedData);
    this.masterAccountsBeforeChange = JSON.parse(JSON.stringify(this.masterAccounts));
    this.saveMasterAccountPreference(updatedData);
    this.masterAccountDropDown.setDisplayValue();
    this.cdr.detectChanges();
  }

  private compare(a: string, b: string) {
    const A = a.toUpperCase();
    const B = b.toUpperCase();

    let comparison = 0;
    if (A > B) {
      comparison = 1;
    } else if (A < B) {
      comparison = -1;
    }
    return comparison;
  }

  saveMasterAccountPreference(maPrefValue: any) {
    if (!maPrefValue)
      maPrefValue = {};

    const pref: any = {
      preferenceName: 'dashboard-master-account',
      preferenceValue: maPrefValue,
      isDeleted: false
    }
    this.pref.saveIrebalPreferences(pref);
  }

  collectModulesToBuild() {
    const modulesDefaultConfig: any = this.getModulesDefaultConfig();
    const dmp: any = this.pref.getPreferenceByName('dashboard-modules-preferences');
    let modulesFromPreferences: any = dmp ? dmp.preferenceValue : [];

    // update info from default
    modulesFromPreferences = modulesFromPreferences.map((prefItem: any) => {
      const defaultItem = modulesDefaultConfig.find((item: any) => item.id === prefItem.id);
      return defaultItem ? { ...prefItem, info: defaultItem.info } : prefItem;
    });

    // add new module from default if any
    const newModules = modulesDefaultConfig.filter((item: any) => !modulesFromPreferences.some((prefItem: any) => prefItem.id === item.id));
    let modules: any = modulesFromPreferences.concat(newModules);

    if (!this.isUiTest)
      modules = this.addModuleClass(modules);
    this.modulesToBuild = modules;
  }

  addModuleClass(modules: any) {
    modules.forEach((module: any)  => {
      switch (module.moduleName) {
        case 'clients':
          module.type = ModuleClientsComponent;
          break;
        case 'automated-rebalances':
          module.type = ModuleAutoRebalancingWidgetComponent;
          break;
        case 'blotter-summary':
          module.type = ModuleBlotterComponent;
          break;
        case 'rebalances':
          module.type = ModuleRebalancingComponent;
          break;
        case 'cash-report':
          module.type = ModuleCashAnalysisComponent;
          break;
        case 'top-models':
          module.type = ModuleModelsComponent;
          break;
        case 'mmc':
          module.type = ModuleMmcComponent;
          break;
        case 'allocation-report':
          module.type = ModuleAllocationComponent;
          break;
      }
    });
    return modules;
  }

  hideAutoTradingModule() {
    const index = this.modulesToBuild.findIndex((module: any) => module.moduleName === 'automated-rebalances');
    if (index !== -1) {
      this.modulesToBuild.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  modulesToSave(customized: any) {
    let modules: any = [];

    const pref: any = {
      preferenceName: 'dashboard-modules-preferences',
      preferenceValue: customized,
      isDeleted: false
    }
    this.pref.saveIrebalPreferences(pref);

    if (!this.isUiTest)
      modules = this.addModuleClass(customized);
    this.modulesToBuild = modules;
    this.cdr.detectChanges();
  }

  getModulesDefaultConfig() {
    this.modulesDefaultConfig = [
      {
        id: 0,
        moduleTitle: 'Clients',
        moduleName: 'clients',
        enabled: true,
        singleWidth: true,
        info: 'Lists the number of unassigned accounts and their total asset amount, the number of portfolios with pending rebalance results, and the number of portfolios with Tax loss harvest expirations in the last seven days.<br>Each tally offers a link to the relevant data table.'
      },
      {
        id: 1,
        moduleTitle: 'Cash Report',
        moduleName: 'cash-report',
        enabled: true,
        singleWidth: true,
        info: 'Offers a tally of accounts with cash amounts in Shortfall, in Excess, or In Range.<br>Each tally offers a link to the relevant data table.'
      },
      {
        id: 2,
        moduleTitle: 'Allocation Report',
        moduleName: 'allocation-report',
        enabled: true,
        singleWidth: true,
        info: 'Shows the number of portfolios with allocations in tolerance and out of tolerance.<br>Each tally offers a link to the relevant data table.'
      },
      {
        id: 3,
        moduleTitle: 'Rebalances',
        moduleName: 'rebalances',
        enabled: true,
        singleWidth: true,
        allowDoubleWidth: true,
        info: 'Lists nine most recent rebalances with name and type, as well as number of portfolios, orders, and approvals associated with that rebalance. This module also offers a direct link to creating a new rebalance.'
      },
      {
        id: 4,
        moduleTitle: 'Automated Rebalances',
        moduleName: 'automated-rebalances',
        enabled: true,
        singleWidth: true,
        allowDoubleWidth: true,
        info: 'Offers an overview of your portfolios with automated rebalancing and recent orders. This includes numbers of portfolios with rebalance errors, portfolios with and without orders, upcoming rebalances, orders with errors, and submitted orders.'
      },
      {
        id: 5,
        moduleTitle: 'Blotter Summary',
        moduleName: 'blotter-summary',
        enabled: true,
        singleWidth: true,
        info: 'Lists your number of active trades, along with a summary of how many are filled, approved, and require attention.<br>Each tally offers a link to the relevant data table.'
      },
      {
        id: 6,
        moduleTitle: 'Top Models',
        moduleName: 'top-models',
        enabled: true,
        singleWidth: true,
        allowDoubleWidth: true,
        info: 'Lists your top models with the number of assigned portfolios and AUM for each model.'
      },
      {
        id: 7,
        moduleTitle: 'Model Market Center',
        moduleName: 'mmc',
        enabled: true,
        singleWidth: true,
        allowDoubleWidth: true,
        info: 'Offers an overview of AUM amount, assigned portfolios, and subscribed models with updates, as well as a monthly billing summary for recent or selected months.'
      }
    ];
    return this.modulesDefaultConfig;
  }



}
